import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, Info, ChevronLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { orders } from "@/lib/api";

const MARKETPLACES = [
  { id: "shopee", name: "Shopee", color: "bg-orange-500", hint: "Ekspor: Semua Pesanan → Ekspor → CSV" },
  { id: "tiktok", name: "TikTok Shop", color: "bg-black", hint: "Seller Center → Orders → Export CSV" },
  { id: "tokopedia", name: "Tokopedia", color: "bg-green-500", hint: "Penjualan → Semua Pesanan → Download Excel/CSV" },
];

export default function OrderImport() {
  const nav = useNavigate();
  const [source, setSource] = useState("shopee");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const onFileChange = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("File harus format CSV");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File maksimal 5MB");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFileChange(f);
  };

  const doImport = async () => {
    if (!file) {
      toast.error("Pilih file CSV dulu");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const res = await orders.importCsv(file, source);
      setResult(res);
      if (res.created_orders > 0) {
        toast.success(`Berhasil import ${res.created_orders} order & ${res.created_customers} customer baru`);
      } else if (res.duplicate_orders > 0) {
        toast.info(`Semua order sudah pernah diimport (${res.duplicate_orders} duplikat)`);
      } else {
        toast.warning("Tidak ada order yang berhasil diimport");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal import CSV");
    }
    setUploading(false);
  };

  const activeMp = MARKETPLACES.find((m) => m.id === source);

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <Link to="/dashboard/orders" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-2">
          <ChevronLeft size={14} /> Kembali ke Orders
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Import Order Marketplace</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload file CSV export dari Shopee, TikTok Shop, atau Tokopedia. Sistem otomatis:
          buat customer baru, deteksi repeat order, & lewati duplikat.
        </p>
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-6 space-y-5">
          <div>
            <Label className="text-sm font-semibold text-gray-900">1. Pilih Marketplace</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {MARKETPLACES.map((mp) => (
                <button
                  key={mp.id}
                  onClick={() => setSource(mp.id)}
                  data-testid={`select-marketplace-${mp.id}`}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    source === mp.id
                      ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-8 h-8 rounded ${mp.color} text-white flex items-center justify-center font-bold text-sm mb-2`}>
                    {mp.name.charAt(0)}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{mp.name}</div>
                </button>
              ))}
            </div>
            {activeMp && (
              <div className="mt-2 flex items-start gap-2 text-xs text-gray-500">
                <Info size={13} className="flex-shrink-0 mt-0.5" />
                <span>{activeMp.hint}</span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm font-semibold text-gray-900">2. Upload File CSV</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragActive ? "border-emerald-500 bg-emerald-50" : "border-gray-300 bg-gray-50/40"
              }`}
              data-testid="csv-dropzone"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="text-emerald-600" size={32} />
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-sm">{file.name}</div>
                    <div className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setFile(null); setResult(null); }} data-testid="clear-file-btn">
                    Ganti
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto text-gray-400" size={40} />
                  <p className="mt-2 text-sm text-gray-600">
                    Tarik file kesini atau{" "}
                    <label className="text-emerald-600 font-semibold cursor-pointer hover:underline">
                      pilih dari komputer
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => onFileChange(e.target.files?.[0])}
                        data-testid="csv-file-input"
                      />
                    </label>
                  </p>
                  <p className="mt-1 text-xs text-gray-400">Format: CSV · Max 5MB</p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => nav("/dashboard/orders")}>Batal</Button>
            <Button
              onClick={doImport}
              disabled={!file || uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="import-csv-btn"
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? "Memproses..." : "Import Sekarang"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="import-result">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="text-emerald-600" size={20} />
              <h3 className="font-heading font-bold text-lg text-gray-900">Hasil Import</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <ResultStat label="Total Baris" value={result.total_rows} />
              <ResultStat label="Customer Baru" value={result.created_customers} accent="emerald" />
              <ResultStat label="Order Baru" value={result.created_orders} accent="emerald" />
              <ResultStat label="Duplikat" value={result.duplicate_orders} accent="amber" />
              <ResultStat label="Invalid" value={result.skipped_invalid} accent="gray" />
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <AlertCircle size={14} className="text-amber-600" />
                  <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Error (max 5)</span>
                </div>
                <ul className="text-xs text-amber-800 space-y-1">
                  {result.errors.map((err, idx) => <li key={`err-${idx}-${err.slice(0, 20)}`}>· {err}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => nav("/dashboard/orders")}>Lihat Orders</Button>
              <Button variant="outline" size="sm" onClick={() => nav("/dashboard/customers")}>Lihat Customer</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-gray-200 bg-gray-50/50">
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Cara Kerja</div>
          <ul className="text-sm text-gray-700 space-y-1.5 list-disc pl-5">
            <li>Sistem otomatis mendeteksi kolom (nomor HP, nama, produk, nilai order, order ID).</li>
            <li>Nomor HP dinormalisasi ke format 62 (mis. 0812 → 6281...).</li>
            <li>Customer baru dibuat dari nomor HP, customer lama akan bertambah order-nya.</li>
            <li>Order dengan Order ID yang sama akan dilewati (dedupe).</li>
            <li>Baris tanpa HP valid atau nilai order akan dilewati.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

const ResultStat = ({ label, value, accent }) => {
  const colors = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    gray: "text-gray-500",
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-bold ${colors[accent] || "text-gray-900"}`}>{value}</div>
    </div>
  );
};
